import * as React from "react";
import { DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog-stacked";
import { cn } from "@/lib/utils";

interface ModalHeaderStackedProps {
  icon: React.ReactNode;
  title: React.ReactNode;
  description?: string;
  /** Icon container variant - 'gradient' for orange tint, 'destructive' for red tint */
  variant?: "gradient" | "destructive";
  className?: string;
}

/**
 * Unified modal header component for stacked dialogs.
 * Use this with dialog-stacked for proper z-index stacking.
 * 
 * @example
 * <ModalHeaderStacked
 *   icon={<Pencil className="h-8 w-8" />}
 *   title="Edit Label"
 *   description="Update the name and color of your tag."
 * />
 */
export function ModalHeaderStacked({
  icon,
  title,
  description,
  variant = "gradient",
  className,
}: ModalHeaderStackedProps) {
  const iconContainerStyles = {
    gradient: "bg-gradient-to-r from-brand-900/10 via-brand-500/10 to-brand-400/10",
    destructive: "bg-destructive/10",
  };

  const iconColorStyles = {
    gradient: "text-brand-accent",
    destructive: "text-destructive",
  };

  return (
    <DialogHeader className={cn("text-left", className)}>
      <div className="flex items-start gap-3">
        <div
          className={cn(
            "mt-0.5 flex h-9 w-9 items-center justify-center rounded-full",
            iconContainerStyles[variant],
          )}
        >
          {React.isValidElement(icon)
            ? React.cloneElement(icon as React.ReactElement<{ className?: string }>, {
                className: cn("h-4 w-4", iconColorStyles[variant], (icon as React.ReactElement<{ className?: string }>).props.className),
              })
            : icon}
        </div>
        <div className="flex-1 space-y-1">
          <DialogTitle className="text-left">{title}</DialogTitle>
          {description && (
            <DialogDescription className="text-left">{description}</DialogDescription>
          )}
        </div>
      </div>
    </DialogHeader>
  );
}

